/**
 * Modal bookkeeping for <Sheet> (components/ui.tsx), kept free of React and the DOM so it can be
 * unit-tested.
 *
 * - The stack of open sheets (bottom → top): only the top one reacts to Escape / Tab, so stacked
 *   sheets (ShareSheet over the layer panel, a ConfirmSheet over anything) close one at a time.
 * - The Tab focus trap decision.
 */

export type OverlayToken = object;

const stack: OverlayToken[] = [];

/** Registers a newly opened overlay on top of the stack; returns its token. */
export function pushOverlay(): OverlayToken {
  const token: OverlayToken = {};
  stack.push(token);
  return token;
}

/** Removes an overlay wherever it is in the stack (a lower sheet may close first). */
export function removeOverlay(token: OverlayToken): void {
  const i = stack.indexOf(token);
  if (i >= 0) stack.splice(i, 1);
}

export function isTopOverlay(token: OverlayToken): boolean {
  return stack.length > 0 && stack[stack.length - 1] === token;
}

export function overlayDepth(): number {
  return stack.length;
}

/**
 * Where Tab (or Shift+Tab) should move focus inside a modal so it never leaves it.
 * `focusables` are the dialog's tabbable elements in order; `active` is the focused element;
 * `inside` says whether `active` is within the dialog; `onPanel` whether the dialog itself has focus.
 * Returns the element to focus, 'panel' to focus the dialog itself, or null to let the browser move
 * focus normally (the next element is still inside).
 */
export function trapTarget<T>(focusables: readonly T[], active: T | null, opts: { inside: boolean; onPanel: boolean; shift: boolean }): T | 'panel' | null {
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (first === undefined || last === undefined) return 'panel';
  if (!opts.inside) return opts.shift ? last : first;
  if (opts.shift && (opts.onPanel || active === first)) return last;
  // Tab from the dialog itself lands on its first focusable naturally (document order).
  if (!opts.shift && !opts.onPanel && active === last) return first;
  return null;
}
